import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, MapPin, Receipt, Leaf, Heart, Star, ArrowRight, Check, Sparkles, Quote, Award } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { REVIEWS, FAQS, AREAS, TRUST_POINTS, SERVICES, COMPANY } from "@/lib/data";

const iconMap = { ShieldCheck, MapPin, Receipt, Leaf, Heart, Award };

export function SectionLabel({ children, className = "" }) {
  return (
    <span className={`inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-[#1E3A6F] ${className}`}>
      <span className="h-px w-8 bg-[#F59E0B]" /> {children}
    </span>
  );
}

export function PageHero({ eyebrow, title, intro, image, imageAlt = "", children }) {
  return (
    <section className="relative bg-white border-b border-slate-100 overflow-hidden" data-testid="page-hero">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
        <div className="relative z-10">
          {eyebrow && <SectionLabel className="mb-5">{eyebrow}</SectionLabel>}
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
            {title}
          </h1>
          {intro && <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">{intro}</p>}
          {children && <div className="mt-8 flex flex-wrap gap-3">{children}</div>}
        </div>
        {image && (
          <div className="relative">
            <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-[#F59E0B]/15 blur-3xl" />
            <div className="relative rounded-3xl overflow-hidden shadow-xl border border-slate-100 aspect-[5/4]">
              <img src={image} alt={imageAlt} className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-lg px-5 py-3 border border-slate-100 hidden sm:flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[#F59E0B]" />
              <span className="text-sm font-medium text-slate-700">Lokal kvalitet sedan länge</span>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export function TrustPointsRow({ items = TRUST_POINTS }) {
  return (
    <section className="py-16 lg:py-20 bg-white border-y border-slate-100" data-testid="trust-points">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-4">
          {items.map((t, i) => {
            const Icon = iconMap[t.icon] || ShieldCheck;
            return (
              <div key={i} className="text-center lg:text-left p-4 group" data-testid={`trust-${i}`}>
                <div className="inline-flex w-12 h-12 rounded-xl items-center justify-center bg-[#1E3A6F]/8 text-[#1E3A6F] mb-4 group-hover:bg-[#F59E0B]/15 group-hover:text-[#1E3A6F] transition-colors">
                  <Icon className="h-5 w-5" strokeWidth={1.7} />
                </div>
                <h4 className="font-display font-semibold text-slate-900 mb-1">{t.title}</h4>
                <p className="text-sm text-slate-600 leading-relaxed">{t.text}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function ServiceCard({ service, index = 0 }) {
  return (
    <Link
      to={service.path}
      className="service-card group block bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all"
      data-testid={`service-card-${service.slug}`}
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div className="aspect-[4/3] overflow-hidden bg-slate-100">
        <img src={service.image} alt={service.title} className="service-card-img w-full h-full object-cover" />
      </div>
      <div className="p-6">
        <h3 className="font-display text-lg font-semibold text-slate-900 mb-1.5">{service.title}</h3>
        <p className="text-sm text-slate-600 leading-relaxed">{service.short}</p>
        <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#1E3A6F] group-hover:gap-2.5 transition-all">
          Läs mer <ArrowRight className="h-4 w-4" />
        </span>
      </div>
    </Link>
  );
}

export function ServicesGrid({ filter = null, title = "Våra tjänster", eyebrow = "Tjänster" }) {
  const list = filter ? SERVICES.filter((s) => filter.includes(s.slug)) : SERVICES;
  return (
    <section className="py-20 lg:py-28" data-testid="services-grid">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {eyebrow && <SectionLabel className="mb-4">{eyebrow}</SectionLabel>}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-12">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 max-w-2xl">
            {title}
          </h2>
          <p className="text-slate-600 max-w-md">Vi anpassar arbetet efter er verksamhet och era ytor – flexibelt, pålitligt och med tydligt resultat.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {list.map((s, i) => <ServiceCard key={s.slug} service={s} index={i} />)}
        </div>
      </div>
    </section>
  );
}

export function ReviewsSection() {
  return (
    <section className="py-20 lg:py-28 bg-white border-y border-slate-100" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <SectionLabel className="justify-center mb-4">Kundröster</SectionLabel>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            Vad våra kunder säger
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {REVIEWS.map((r, i) => (
            <div
              key={i}
              className="relative bg-[#F8FAFC] rounded-2xl p-8 border border-slate-100 hover:border-[#F59E0B]/40 transition-colors"
              data-testid={`review-${i}`}
            >
              <Quote className="absolute top-6 right-6 h-8 w-8 text-[#F59E0B]/30" />
              <div className="flex gap-0.5 mb-4">
                {[...Array(5)].map((_, k) => (
                  <Star key={k} className="h-4 w-4 fill-[#F59E0B] text-[#F59E0B]" />
                ))}
              </div>
              <p className="text-slate-700 leading-relaxed mb-5 font-medium">"{r.text}"</p>
              <p className="text-sm text-slate-500">— {r.author}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FAQSection({ items = FAQS }) {
  return (
    <section className="py-20 lg:py-28" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <SectionLabel className="justify-center mb-4">Vanliga frågor</SectionLabel>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            Här hittar du svaren
          </h2>
        </div>
        <Accordion type="single" collapsible className="space-y-3">
          {items.map((f, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              className="bg-white border border-slate-100 rounded-xl px-6 data-[state=open]:border-[#1E3A6F]/30 data-[state=open]:shadow-sm transition-all"
              data-testid={`faq-${i}`}
            >
              <AccordionTrigger className="text-left font-medium text-slate-900 hover:no-underline py-5">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pb-5">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

export function AreaMap() {
  return (
    <section className="py-20 lg:py-28 bg-[#1E3A6F] text-white relative overflow-hidden" data-testid="area-map">
      <div className="absolute inset-0 bg-grain opacity-30 mix-blend-overlay" />
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#F59E0B]/10 blur-3xl" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <SectionLabel className="!text-[#F59E0B] mb-5">Områden vi arbetar i</SectionLabel>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mb-6">
              Lokala i Bjuv – aktiva i hela nordvästra Skåne
            </h2>
            <p className="text-white/80 leading-relaxed mb-4 max-w-xl">
              Med utgångspunkt i Bjuv hjälper Rena Tjänster företag och verksamheter i nordvästra
              Skåne, bland annat i Helsingborg, Höganäs, Ängelholm, Båstad, Örkelljunga, Perstorp,
              Klippan, Svalöv, Landskrona och Åstorp.
            </p>
            <p className="text-white/70 leading-relaxed mb-8 max-w-xl">
              Är ni osäkra på om vi täcker just er ort? Hör av er – vi löser det oftast ändå.
            </p>
            <div className="flex flex-wrap gap-2">
              {[...new Set(AREAS)].map((c) => (
                <span
                  key={c}
                  className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-sm border border-white/15 hover:bg-[#F59E0B] hover:text-[#1E3A6F] hover:border-[#F59E0B] transition-colors"
                  data-testid={`area-${c}`}
                >
                  {c}
                </span>
              ))}
            </div>
          </div>
          {/* Stylized map / visual area */}
          <div className="relative">
            <div className="aspect-square max-w-md mx-auto relative">
              <svg viewBox="0 0 400 400" className="w-full h-full">
                <defs>
                  <radialGradient id="rg" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#F59E0B" stopOpacity="0.25" />
                    <stop offset="100%" stopColor="#F59E0B" stopOpacity="0" />
                  </radialGradient>
                </defs>
                {/* Concentric rings */}
                <circle cx="200" cy="200" r="180" fill="none" stroke="rgba(255,255,255,0.08)" strokeDasharray="2 6" />
                <circle cx="200" cy="200" r="130" fill="none" stroke="rgba(255,255,255,0.10)" strokeDasharray="2 6" />
                <circle cx="200" cy="200" r="80" fill="none" stroke="rgba(255,255,255,0.12)" />
                <circle cx="200" cy="200" r="180" fill="url(#rg)" />

                {/* Cities (approximate positions) */}
                {[
                  { name: "Bjuv", x: 200, y: 200, main: true },
                  { name: "Helsingborg", x: 130, y: 175 },
                  { name: "Åstorp", x: 250, y: 180 },
                  { name: "Ängelholm", x: 180, y: 110 },
                  { name: "Höganäs", x: 110, y: 130 },
                  { name: "Klippan", x: 290, y: 220 },
                  { name: "Landskrona", x: 150, y: 270 },
                  { name: "Båstad", x: 240, y: 80 },
                  { name: "Svalöv", x: 280, y: 270 },
                  { name: "Perstorp", x: 325, y: 175 },
                  { name: "Örkelljunga", x: 305, y: 130 },
                ].map((c) => (
                  <g key={c.name}>
                    <circle
                      cx={c.x}
                      cy={c.y}
                      r={c.main ? 7 : 4}
                      fill={c.main ? "#F59E0B" : "#ffffff"}
                    />
                    {c.main && <circle cx={c.x} cy={c.y} r="14" fill="none" stroke="#F59E0B" strokeOpacity="0.5">
                      <animate attributeName="r" from="7" to="22" dur="2s" repeatCount="indefinite" />
                      <animate attributeName="stroke-opacity" from="0.6" to="0" dur="2s" repeatCount="indefinite" />
                    </circle>}
                    <text x={c.x + (c.main ? 12 : 8)} y={c.y + 4} fill="white" fontSize="11" fontFamily="Work Sans" opacity={c.main ? 1 : 0.75} fontWeight={c.main ? 600 : 400}>
                      {c.name}
                    </text>
                  </g>
                ))}
              </svg>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function CheckList({ items, columns = 1 }) {
  return (
    <ul className={`grid ${columns === 2 ? "sm:grid-cols-2" : ""} gap-3`}>
      {items.map((t, i) => (
        <li key={i} className="flex items-start gap-3" data-testid={`check-${i}`}>
          <span className="mt-0.5 inline-flex w-5 h-5 rounded-full bg-[#1E3A6F]/10 text-[#1E3A6F] items-center justify-center flex-shrink-0">
            <Check className="h-3 w-3" strokeWidth={3} />
          </span>
          <span className="text-slate-700 leading-relaxed">{t}</span>
        </li>
      ))}
    </ul>
  );
}

export function CTABlock({ title, description }) {
  return (
    <section className="py-20 lg:py-28 bg-gradient-to-br from-[#F8FAFC] to-white border-t border-slate-100" data-testid="cta-block">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8 lg:p-14 grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-2">
            <SectionLabel className="mb-4">Kostnadsfri offert</SectionLabel>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mb-4">{title}</h2>
            <p className="text-slate-600 leading-relaxed">{description}</p>
            <div className="mt-6 space-y-2 text-sm">
              <p className="flex items-center gap-2 text-slate-700"><Award className="h-4 w-4 text-[#F59E0B]" /> ID06-legitimerad personal</p>
              <p className="flex items-center gap-2 text-slate-700"><ShieldCheck className="h-4 w-4 text-[#F59E0B]" /> Försäkring upp till 10 miljoner</p>
              <p className="flex items-center gap-2 text-slate-700"><MapPin className="h-4 w-4 text-[#F59E0B]" /> Lokal städfirma i Bjuv</p>
            </div>
          </div>
          <div className="lg:col-span-3">
            <Link
              to="/kontakt#offertformular"
              className="block"
              data-testid="cta-block-link"
            >
              <Button className="w-full bg-[#1E3A6F] hover:bg-[#152a51] text-white rounded-full px-8 py-7 text-base font-medium transition-all hover:-translate-y-0.5">
                Begär offert
              </Button>
            </Link>
            <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
              <a href={COMPANY.phoneLink} className="bg-slate-50 rounded-xl p-4 hover:bg-slate-100 transition-colors" data-testid="cta-call">
                <p className="text-xs text-slate-500 mb-1">Ring oss direkt</p>
                <p className="font-display font-semibold text-slate-900">{COMPANY.phone}</p>
              </a>
              <a href={`mailto:${COMPANY.email}`} className="bg-slate-50 rounded-xl p-4 hover:bg-slate-100 transition-colors" data-testid="cta-email">
                <p className="text-xs text-slate-500 mb-1">E-posta oss</p>
                <p className="font-display font-semibold text-slate-900 text-sm truncate">{COMPANY.email}</p>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
